import { useCallback, useEffect, useState } from "react";
import { FolderOpen, ExternalLink, Download, Star, FolderInput, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useExportDownload } from "@/hooks/useExportDownload";
import { infoSluzby, podklad } from "@/lib/odkazy";

// 313: Hlavní dokumenty v Informacích zakázky — složky a soubory s hvězdičkou z Dokumentů.
// Nahrazuje starou kartu Odkazy (162, `projects.links`): co v ní zbylo, je vidět dál
// a vedení to jedním klepnutím přesune do Dokumentů (rovnou s hvězdičkou).
const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; } };

function Radek({ ikona, nazev, popis, href, onClick, akce, testid }) {
  const obsah = (
    <>
      {ikona}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold truncate">{nazev}</span>
        <span className="block text-xs text-muted-foreground truncate">{popis}</span>
      </span>
    </>
  );
  const cls = "flex-1 min-w-0 flex items-center gap-2.5 rounded-[10px] px-1.5 py-1.5 -mx-1.5 hover:bg-secondary/60 transition-colors motion-reduce:transition-none text-left";
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      {href ? <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{obsah}</a>
        : <button type="button" onClick={onClick} className={cls}>{obsah}</button>}
      {akce}
    </div>
  );
}

const Stitek = ({ url, slozka }) => {
  const s = infoSluzby(url);
  return (
    <span className="w-9 h-9 shrink-0 grid place-items-center rounded-[10px] text-[10.5px] font-extrabold dark:brightness-[1.7]"
      style={{ background: podklad(s.barva), color: s.barva }}>
      {slozka ? <FolderOpen size={17} /> : s.zkratka}
    </span>
  );
};

export function HlavniDokumenty({ project, manager, canEdit, onGoDocs, onSaved }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { busy: downloading, run: runDownload } = useExportDownload();
  const load = useCallback(() => {
    api.get(`/projects/${project.id}/documents-files`).then((r) => setData(r.data)).catch(() => setData({ documents: [] }));
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const stare = project.links || [];
  if (!data) return null;
  const cats = Object.fromEntries((data.categories || []).map((c) => [c.value, c.label]));
  const slozky = [...(data.folder?.star ? [{ ...data.folder, id: "slozka", name: "Složka zakázky" }] : []),
    ...(data.documents || []).filter((d) => d.folder && d.star)];
  const soubory = (data.documents || []).filter((d) => !d.folder && d.star);
  if (!slozky.length && !soubory.length && !stare.length && !manager) return null;

  // Starý odkaz → karta v Dokumentech s hvězdičkou; ze staré karty zmizí.
  const presun = async (l) => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/documents-links`, { url: l.url, name: l.title, category: "ostatni", star: true });
      await api.patch(`/projects/${project.id}`, { name: project.name,
        links: stare.filter((x) => x.id !== l.id).map((x) => ({ id: x.id, title: x.title, url: x.url })) });
      toast.success("Přesunuto do Dokumentů.");
      load(); onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-[14px] p-4" data-testid="info-hlavni">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-[15px] font-bold inline-flex items-center gap-1.5"><Star size={15} className="text-amber-500" fill="currentColor" /> Hlavní dokumenty</h3>
        {manager && <button type="button" onClick={onGoDocs} data-testid="info-hlavni-vse" className="text-[13px] font-bold text-brand hover:underline">Všechny dokumenty</button>}
      </div>

      {!slozky.length && !soubory.length && !stare.length && (
        <p className="text-sm text-muted-foreground" data-testid="info-hlavni-prazdne">
          V Dokumentech označte hvězdičkou složky a soubory, které má mít tým po ruce — ukážou se tady.
        </p>
      )}

      <div className="space-y-1">
        {slozky.map((f) => (
          <Radek key={f.id} testid={`info-hlavni-${f.id}`} href={f.url} ikona={<Stitek url={f.service || f.url} slozka />}
            nazev={f.name} popis={`Složka · ${infoSluzby(f.service || f.url).nazev}`}
            akce={<ExternalLink size={15} className="text-muted-foreground shrink-0 mx-1.5" aria-hidden />} />
        ))}
        {soubory.map((d) => d.kind === "LINK" ? (
          <Radek key={d.id} testid={`info-hlavni-${d.id}`} href={d.url} ikona={<Stitek url={d.service || d.url} />}
            nazev={d.name} popis={`${cats[d.category] || "Dokument"} · ${infoSluzby(d.service || d.url).nazev}`}
            akce={<ExternalLink size={15} className="text-muted-foreground shrink-0 mx-1.5" aria-hidden />} />
        ) : (
          <Radek key={d.id} testid={`info-hlavni-${d.id}`} onClick={() => !downloading && runDownload(d.id, d.url, d.name, "Stahuji dokument…")}
            ikona={<span className="w-9 h-9 shrink-0 grid place-items-center rounded-[10px] bg-secondary text-brand"><Download size={16} /></span>}
            nazev={d.name} popis={`${cats[d.category] || "Dokument"} · nahraný soubor`} />
        ))}
        {stare.map((l) => (
          <Radek key={l.id} testid={`info-hlavni-stary-${l.id}`} href={l.url}
            ikona={<span className="w-9 h-9 shrink-0 grid place-items-center rounded-[10px] bg-secondary text-muted-foreground"><Link2 size={16} /></span>}
            nazev={l.title} popis={hostOf(l.url)}
            akce={canEdit && manager ? (
              <button type="button" onClick={() => presun(l)} disabled={busy} data-testid={`info-hlavni-presun-${l.id}`}
                title="Přesunout do Dokumentů (s hvězdičkou)" aria-label="Přesunout do Dokumentů"
                className="h-9 w-9 shrink-0 grid place-items-center rounded-[8px] text-muted-foreground hover:text-brand hover:bg-secondary disabled:opacity-50">
                <FolderInput size={16} />
              </button>
            ) : null} />
        ))}
      </div>
      {stare.length > 0 && canEdit && manager && (
        <p className="text-[12px] text-muted-foreground mt-2">Starší odkazy ze zakázky — ikonou vpravo je přesunete do Dokumentů.</p>
      )}
    </div>
  );
}
